import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  MenuItem,
  Select,
  TextField,
  Typography,
  Divider,
  Autocomplete,
  FormControl,
  SelectChangeEvent,
} from "@mui/material";
import { FileDownload, Print } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import * as xlsx from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import DynamicTable from "../../../components/lab-dynamic-table"; // Ensure this path is correct
import { fetchProfileData } from "../../../redux/reducers/profileSlice";
import { formatDateTime } from "../../../utils/dateTimeUtils";
import { formatCurrency } from "../../../utils/numberUtils";
import DateRangeSelector from "../../../components/DateRangeSelector";

// Payment detail interface
interface PaymentDetail {
  grossAmount: number;
  discountInPercentage: number;
  discountInAmount: number;
  nowPaying: number;
  netAmount: number;
  dueAmount: number;
}

// Patient information interface
interface Patient {
  mrn: number;
  patientName: string;
  guardiansName: string;
  phonNumber: string;
}

// Created by interface
interface CreatedBy {
  _id: string;
  fullName: string;
  firstName?: string;
}

// Referring doctor master record
interface ReferringDoctor {
  _id: string;
  doctorName: string;
}

// Main interface for the lab report data
interface LabReportData {
  _id: string;
  paymentDetail: PaymentDetail;
  userId: string;
  patient: Patient;
  createdAt: string;
  createdBy: CreatedBy;
  collectionCenter: {
    _id: string;
    collectionCenterName: string;
  };
  otherDetails?: {
    referredBy?: string;
    referringDrId?: string | null;
  };
}

const UserReport: React.FC = () => {
  const dispatch = useDispatch();
  const profile = useSelector((state: any) => state.profileSlice.profile);
  const labPermissions = profile?.data?.accessConfigurations?.Lab;
  const currentUserId = profile?.data?._id;
  const currentUserName = profile?.data?.fullName;

  const today = new Date().toISOString().split("T")[0];

  const [summaryData, setSummaryData] = useState<LabReportData[]>([]);
  const [loading, setLoading] = useState(false);

  // State for date filtering
  const [dateRange, setDateRange] = useState("Today");
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [users, setUsers] = useState<any[]>([]);
  const [userId, setUserId] = useState<string>("");
  const [referringDoctors, setReferringDoctors] = useState<ReferringDoctor[]>([]);
  const [referringDrId, setReferringDrId] = useState<string>("");
  const [selectedCollectionCenterId, setSelectedCollectionCenterId] =
    useState<string>("");
  const [selectedCollectionCenterIdNew, setSelectedCollectionCenterIdNew] =
    useState<string>("all");
      const collectionCenters = profile?.data?.collectionCenter || [];
      const centerName = collectionCenters.find((cc:any) => cc._id === selectedCollectionCenterId)?.collectionCenterName || "--";
      const selectedReferringDoctorName =
        referringDoctors.find(doc => doc._id === referringDrId)?.doctorName || "All";

      

    const collectioncenterName =
      profile?.data?.collectionCenter?.find((cc:any) => cc._id === selectedCollectionCenterId)?.name || "All";
  // State for pagination
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(100);
  const [totalCount, setTotalCount] = useState<number>(0);

  // State for sorting
  const [sort, setSort] = useState<string>("descending");

  const fetchUsers = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_ADMIN_API_URL}/users/getHospitalAllUsers`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setUsers(response.data?.data || []);
    } catch (error: any) {
      console.error("Error fetching doctors:", error);
      toast.error(error?.response.data.message || "Error fetching users");
    }
  };

  const fetchReferringDoctors = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_LAB_URL}/referring-doctor/getAllBySearch`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setReferringDoctors(response.data?.data || []);
    } catch (error: any) {
      console.error("Error fetching referring doctors:", error);
      setReferringDoctors([]);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchReferringDoctors();
  }, []);

  // Set current user automatically if can_view_all_users_reports is false
  useEffect(() => {
    if (
      labPermissions?.can_view_all_users_reports === false &&
      currentUserId &&
      !userId
    ) {
      setUserId(currentUserId);
    }
  }, [labPermissions?.can_view_all_users_reports, currentUserId, userId]);

  // Fetch profile data on component mount
  useEffect(() => {
    dispatch(fetchProfileData() as any);
  }, [dispatch]);
  const handleCollectionCenterChange = (event: SelectChangeEvent) => {
    const selectedValue = event.target.value;
    setSelectedCollectionCenterIdNew(selectedValue);
    
    // If "all" is selected, clear the main dropdown
    if (selectedValue === "all") {
      setSelectedCollectionCenterId("");
    } else {
      // Sync with the main dropdown
      setSelectedCollectionCenterId(selectedValue);
    }
  };

  const fetchSummaryData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_LAB_URL}/dashboard/getLabStateUserWizeWithDetails`,
        {
          params: {
            fromDate,
            toDate,
            pageNo: page + 1, // API is 1-based, component is 0-based
            count: rowsPerPage,
            sort,
            ...(selectedCollectionCenterIdNew !== "all" && { collectionCenterId: selectedCollectionCenterId }),
            ...(userId && { userId }),
            ...(referringDrId && { referringDrId }),
          },
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      if (response.data.isSuccess) {
        setSummaryData(response.data.data || []);
        setTotalCount(response.data.totalCount || 0);
      } else {
        toast.error(response.data.message || "Failed to fetch summary");
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Error fetching summary data");
      setSummaryData([]);
    } finally {
      setLoading(false);
    }
  };

  // Refetch data when dependencies like date, page, or sort order change
  useEffect(() => {
    if (selectedCollectionCenterIdNew === "all" || selectedCollectionCenterId) {
      fetchSummaryData();
    }
  }, [fromDate, toDate, page, rowsPerPage, userId, referringDrId, selectedCollectionCenterId, selectedCollectionCenterIdNew]);

  // Handlers for pagination
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // The referring doctor name is stored on the sample collection itself; fall back to
  // the master record when only the id was saved.
  const getReferringDoctorName = (row: LabReportData) =>
    row.otherDetails?.referredBy ||
    referringDoctors.find(doc => doc._id === row.otherDetails?.referringDrId)
      ?.doctorName ||
    "-";

  // Note: Exports only the currently visible page. For a full export, a separate API call without pagination would be needed.
  const handleExcelExport = () => {
    if (!summaryData || summaryData.length === 0) {
      toast.warn("No data to export.");
      return;
    }

    // Calculate totals
    const totals = summaryData.reduce(
      (acc, row) => ({
        grossAmount: acc.grossAmount + row.paymentDetail.grossAmount,
        discountAmount: acc.discountAmount + row.paymentDetail.discountInAmount,
        nowPaying: acc.nowPaying + row.paymentDetail.nowPaying,
        netAmount: acc.netAmount + row.paymentDetail.netAmount,
        dueAmount: acc.dueAmount + row.paymentDetail.dueAmount,
      }),
      {
        grossAmount: 0,
        discountAmount: 0,
        nowPaying: 0,
        netAmount: 0,
        dueAmount: 0,
      }
    );

    const worksheetData = summaryData?.map((row, index) => ({
      SN: page * rowsPerPage + index + 1,
      Center: row.collectionCenter?.collectionCenterName || "-",
      MRN: row.patient?.mrn,
      "Patient Name": row.patient?.patientName,
      "Guardian Name": row.patient?.guardiansName,
      "Phone Number": row.patient?.phonNumber,
      "Gross Amount": row.paymentDetail.grossAmount,
      "Discount Amount": row.paymentDetail.discountInAmount,
      "Net Amount": row.paymentDetail.netAmount,
      "Paid Amount": row.paymentDetail.nowPaying,
      "Due Amount": row.paymentDetail.dueAmount,
      "Referring Doctor": getReferringDoctorName(row),
      "Created By": row.createdBy.fullName || row.createdBy.firstName,
      "Created At": formatDateTime(row.createdAt),
    }));

    // Add totals row
    worksheetData.push({
      SN: "" as any,
      Center: "TOTAL:",
      MRN: null as any, // Use null for empty MRN in totals row
      "Patient Name": "",
      "Guardian Name": "",
      "Phone Number": "",
      "Gross Amount": totals.grossAmount,
      "Discount Amount": totals.discountAmount,
      "Net Amount": totals.netAmount,
      "Paid Amount": totals.nowPaying,
      "Due Amount": totals.dueAmount,
      "Referring Doctor": "",
      "Created By": "",
      "Created At": "",
    });

    const worksheet = xlsx.utils.json_to_sheet(worksheetData);

    // Style the totals row (make it bold)
    const totalRowIndex = worksheetData.length;
    const range = xlsx.utils.decode_range(worksheet["!ref"] || "A1");

    // Apply bold styling to the total row
    for (let col = range.s.c; col <= range.e.c; col++) {
      const cellAddress = xlsx.utils.encode_cell({
        r: totalRowIndex - 1,
        c: col,
      });
      if (!worksheet[cellAddress]) worksheet[cellAddress] = { t: "s", v: "" };
      worksheet[cellAddress].s = {
        font: { bold: true },
        fill: { fgColor: { rgb: "FFFFD700" } }, // Light yellow background
      };
    }

    const workbook = xlsx.utils.book_new();
    xlsx.utils.book_append_sheet(workbook, worksheet, "LabReports");
    xlsx.writeFile(workbook, `Lab_Reports_${fromDate}_to_${toDate}.xlsx`);
  };

  const handlePrint = () => {
    if (!summaryData || summaryData.length === 0) {
      toast.warn("No data to print.");
      return;
    }

    // Calculate totals
    const totals = summaryData.reduce(
      (acc, row) => ({
        grossAmount: acc.grossAmount + row.paymentDetail.grossAmount,
        discountAmount: acc.discountAmount + row.paymentDetail.discountInAmount,
        nowPaying: acc.nowPaying + row.paymentDetail.nowPaying,
        netAmount: acc.netAmount + row.paymentDetail.netAmount,
        dueAmount: acc.dueAmount + row.paymentDetail.dueAmount,
      }),
      {
        grossAmount: 0,
        discountAmount: 0,
        nowPaying: 0,
        netAmount: 0,
        dueAmount: 0,
      }
    );

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 8;
    let yPosition = margin;

    // Professional header with minimal, clean design
    doc
      .setFontSize(10)
      .setFont("helvetica", "bold")
      .setTextColor(25, 55, 85)
      .text("LAB REPORTS SUMMARY", pageWidth / 2, yPosition, {
        align: "center",
      });


      doc.setFontSize(8)
      .text(
        `Collection Center: ${selectedCollectionCenterIdNew === "all" ? "All " : centerName || "--"}  | Referring Doctor: ${selectedReferringDoctorName}`,
        pageWidth / 2,
        yPosition+5,
        { align: "center" }
      );
    
    yPosition += 6;
    doc
      .setDrawColor(25, 55, 85)
      .setLineWidth(0.5)
      .line(margin, yPosition, pageWidth - margin, yPosition);

    // Subtle metadata section with smaller font
    yPosition += 3;
    doc
      .setFontSize(6.5)
      .setFont("helvetica", "normal")
      .setTextColor(80, 80, 80)
      .text(
        `Report Period: ${fromDate} to ${toDate} | Generated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`,
        pageWidth / 2,
        yPosition,
        { align: "center" }
      );

    yPosition += 6;

    const head = [
      [
        "SN",
        "CENTER",
        "MRN",
        "PATIENT NAME",
        "GUARDIAN NAME",
        "PHONE NUMBER",
        "GROSS AMOUNT",
        "DISCOUNT AMOUNT",
        "NET AMOUNT",
        "PAID AMOUNT",
        "DUE AMOUNT",
        "REFERRING DOCTOR",
        "CREATED BY",
        "CREATED AT",
      ],
    ];

    const body = summaryData.map((row, index) => [
      page * rowsPerPage + index + 1,
      row.collectionCenter?.collectionCenterName ?? "-",
      row.patient?.mrn ?? "",
      row.patient?.patientName ?? "",
      row.patient?.guardiansName ?? "",
      row.patient?.phonNumber ?? "",
      formatCurrency(row.paymentDetail.grossAmount ?? 0),
      formatCurrency(row.paymentDetail.discountInAmount ?? 0),
      formatCurrency(row.paymentDetail.netAmount ?? 0),
      formatCurrency(row.paymentDetail.nowPaying ?? 0),
      formatCurrency(row.paymentDetail.dueAmount ?? 0),
      getReferringDoctorName(row),
      row.createdBy?.fullName ?? row.createdBy?.firstName ?? "",
      formatDateTime(row.createdAt),
    ]);

    // Add totals row
    body.push([
      "",
      "TOTAL",
      "",
      "",
      "",
      "",
      formatCurrency(totals.grossAmount),
      formatCurrency(totals.discountAmount),
      formatCurrency(totals.netAmount),
      formatCurrency(totals.nowPaying),
      formatCurrency(totals.dueAmount),
      "",
      "",
      "",
    ]);

    // Clean, professional table styling
    autoTable(doc, {
      startY: yPosition,
      head,
      body,
      headStyles: { 
        fillColor: [25, 55, 85],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 6,
        halign: 'center',
        valign: 'middle',
        cellPadding: 2
      },
      bodyStyles: {
        fontSize: 5.5,
        textColor: [40, 40, 40],
        cellPadding: 1.8
      },
      columnStyles: {
        4: { halign: "center" },
        5: { halign: "center" },
        6: { halign: "center" },
        7: { halign: "center" },
        8: { halign: "center" },
      },
      alternateRowStyles: {
        fillColor: [248, 249, 250]
      },
      margin: { top: margin, right: margin, bottom: margin, left: margin },
      didParseCell: function(data) {
        // Professional total row with premium styling
        if (data.row.index === body.length - 1) {
          data.cell.styles.fillColor = [25, 55, 85];
          data.cell.styles.textColor = [255, 255, 255];
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fontSize = 5.8;
          data.cell.styles.lineColor = [200, 200, 200];
          data.cell.styles.lineWidth = 0.3;
          (data.cell.styles as any).cellPadding = { top: 2.5, right: 2, bottom: 2.5, left: 2 };
        }
      },
      didDrawPage: function(data) {
        // Add subtle footer with page number
        const pageSize = doc.internal.pageSize;
        const pageCount = doc.getNumberOfPages();
        const pageNum = data.pageNumber;
        
        doc.setFontSize(5);
        doc.setTextColor(120, 120, 120);
        doc.text(
          `Page ${pageNum} of ${pageCount}`,
          pageSize.getWidth() / 2,
          pageSize.getHeight() - 4,
          { align: "center" }
        );
      }
    });

    doc.autoPrint();
    const blob = doc.output("blob");
    const blobUrl = URL.createObjectURL(blob);
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "none";
    document.body.appendChild(iframe);
    
    const cleanup = () => {
      if (document.body.contains(iframe)) {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(blobUrl);
      }
      window.removeEventListener("focus", cleanup);
    };
    
    window.addEventListener("focus", cleanup);
    iframe.src = blobUrl;
    
    iframe.onload = () => {
      try {
        const win = iframe.contentWindow;
        if (win) {
          win.focus();
          win.print();
        }
      } catch (e) {
        console.error("Error triggering print dialog:", e);
        cleanup();
      }
    };
    
    // Fallback print trigger
    setTimeout(() => {
      try {
        const win = iframe.contentWindow;
        if (win && document.body.contains(iframe)) {
          win.focus();
          win.print();
        }
      } catch (e) {
        console.error("Fallback print attempt failed:", e);
        cleanup();
      }
    }, 1000);
  };

  // Calculate total net amount
  const totalNetAmount = summaryData.reduce(
    (sum, item) => sum + item.paymentDetail.nowPaying,
    0
  );

  // Prepare data for the DynamicTable component
  const tableData: any[][] = summaryData?.map((item, index) => [
    page * rowsPerPage + index + 1,
    item.collectionCenter?.collectionCenterName || "-",
    item?.patient?.mrn,
    item.patient?.patientName,
    item.patient?.guardiansName,
    item.patient?.phonNumber,
    ` ${item.paymentDetail.grossAmount?.toLocaleString()}`,
    ` ${item.paymentDetail.discountInAmount?.toLocaleString()}`,
    ` ${item.paymentDetail.netAmount?.toLocaleString()}`,
    ` ${item.paymentDetail.nowPaying?.toLocaleString()}`,
    ` ${item.paymentDetail.dueAmount?.toLocaleString()}`,
    getReferringDoctorName(item),
    item.createdBy.fullName || item.createdBy.firstName,
    formatDateTime(item.createdAt),
  ]);

  // Add total row at the end if data exists
  if (summaryData && summaryData.length > 0) {
    const totals = summaryData.reduce(
      (acc, row) => ({
        grossAmount: acc.grossAmount + (row.paymentDetail.grossAmount || 0),
        discountAmount: acc.discountAmount + (row.paymentDetail.discountInAmount || 0),
        nowPaying: acc.nowPaying + (row.paymentDetail.nowPaying || 0),
        netAmount: acc.netAmount + (row.paymentDetail.netAmount || 0),
        dueAmount: acc.dueAmount + (row.paymentDetail.dueAmount || 0),
      }),
      { grossAmount: 0, discountAmount: 0, nowPaying: 0, netAmount: 0, dueAmount: 0 }
    );

    tableData.push([
      <Box component="span" sx={{ fontWeight: "bold" }}>-</Box>,
      <Box component="span" sx={{ fontWeight: "bold" }}>TOTAL</Box>,
      "",
      "",
      "",
      "",
      <Box component="span" sx={{ fontWeight: "bold" }}>{totals.grossAmount.toLocaleString()}</Box>,
      <Box component="span" sx={{ fontWeight: "bold" }}>{totals.discountAmount.toLocaleString()}</Box>,
      <Box component="span" sx={{ fontWeight: "bold" }}>{totals.netAmount.toLocaleString()}</Box>,
      <Box component="span" sx={{ fontWeight: "bold" }}>{totals.nowPaying.toLocaleString()}</Box>,
      <Box component="span" sx={{ fontWeight: "bold" }}>{totals.dueAmount.toLocaleString()}</Box>,
      "",
      "",
      "",
    ]);
  }

  // Reusable button styles
  const gradientButtonStyle = {
    minWidth: { xs: "auto", md: "80px" },
    fontSize: { xs: "12px", md: "12px" },
    fontWeight: "600",
    textTransform: "none",
    borderRadius: "8px",
    background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
    color: "#ffffff",
    boxShadow: "0 3px 5px 2px rgba(25, 118, 210, .3)",
    transition: "all 0.3s ease-in-out",
    "&:hover": {
      background: "linear-gradient(45deg, #1565c0 30%, #1e88e5 90%)",
      boxShadow: "0 5px 15px rgba(25, 118, 210, .4)",
      transform: "translateY(-1px)",
    },
  };

  return (
    <Box sx={{ background: "#fff", borderRadius: "12px" }}>
      {/* --- CHANGE 1: REMOVED THE HEADER AND DIVIDER THAT WERE HERE --- */}

      <Box
        sx={{
          p: 1,
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", gap: 1 }}>
          {/* --- CHANGE 2: APPLIED GRADIENT STYLES TO BUTTONS --- */}
          <Button
            variant="contained"
            size="small"
            startIcon={<FileDownload />}
            onClick={handleExcelExport}
            sx={gradientButtonStyle}
          >
            Excel
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<Print />}
            onClick={handlePrint}
            sx={gradientButtonStyle}
          >
            Print
          </Button>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            gap: 2,
            flexWrap: "wrap",
            justifyContent: "flex-end",
            flexDirection: { xs: "column", sm: "row" },
            width: { xs: "100%", sm: "auto" },
          }}
        >
          <Box sx={{ width: { xs: "100%", sm: "auto" } }}>
            <Typography sx={{ fontSize: "12px", fontWeight: "600", mb: 0.5 }}>
              Referring Doctor
            </Typography>
            <Autocomplete
              size="small"
              fullWidth
              options={referringDoctors}
              getOptionLabel={option => option.doctorName || ""}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              value={
                referringDoctors.find(doc => doc._id === referringDrId) || null
              }
              onChange={(event, newValue) => {
                setReferringDrId(newValue ? newValue._id : "");
                setPage(0);
              }}
              sx={{ minWidth: { xs: "100%", sm: "200px" } }}
              renderInput={params => (
                <TextField
                  {...params}
                  className="custom-input"
                  placeholder="All Doctors"
                />
              )}
            />
          </Box>
          <Box sx={{ width: { xs: "100%", sm: "auto" } }}>
            <Typography sx={{ fontSize: "12px", fontWeight: "600", mb: 0.5 }}>
              Collection Center
            </Typography>
            <FormControl
              className="custom-input"
              size="small"
              fullWidth
              sx={{ minWidth: { xs: "100%", sm: "200px" } }}
            >
              <Select
                value={selectedCollectionCenterIdNew}
                onChange={handleCollectionCenterChange}
                displayEmpty
              >
                <MenuItem value="all">All</MenuItem>
                {collectionCenters.map((center: any) => (
                  <MenuItem key={center._id} value={center._id}>
                    {center.collectionCenterName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ width: { xs: "100%", sm: "auto" } }}>
            <Typography sx={{ fontSize: "12px", fontWeight: "600", mb: 0.5 }}>
              Select user
            </Typography>
            <Autocomplete
              size="small"
              fullWidth
              disabled={labPermissions?.can_view_all_users_reports === false}
              options={
                labPermissions?.can_view_all_users_reports === false
                  ? currentUserId
                    ? [{ _id: currentUserId, fullName: currentUserName }]
                    : []
                  : users
              }
              getOptionLabel={option => option.fullName || ""}
              value={
                labPermissions?.can_view_all_users_reports === false
                  ? currentUserId
                    ? { _id: currentUserId, fullName: currentUserName }
                    : null
                  : users?.find(doc => doc._id === userId) || null
              }
              onChange={(event, newValue) => {
                if (labPermissions?.can_view_all_users_reports !== false) {
                  setUserId(newValue ? newValue._id : "");
                }
              }}
              sx={{
                minWidth: { xs: "100%", sm: "200px" },
                "& .MuiAutocomplete-input.Mui-disabled": {
                  color: "rgba(0, 0, 0, 0.6)",
                  WebkitTextFillColor: "rgba(0, 0, 0, 0.6)",
                },
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  className="custom-input"
                  placeholder={
                    labPermissions?.can_view_all_users_reports === false
                      ? "Current User Only"
                      : "Select user"
                  }
                />
              )}
            />
          </Box>
          <DateRangeSelector
            preset="lab"
            dateRange={dateRange}
            fromDate={fromDate}
            toDate={toDate}
            onDateRangeChange={(value) => {
              setDateRange(value);
              setPage(0);
            }}
            onFromDateChange={setFromDate}
            onToDateChange={setToDate}
          />
        </Box>
      </Box>

      <Box
        sx={{
          p: 1,
          maxHeight: "60vh",
          overflowY: "hidden",
        }}
      >
        <DynamicTable
          headers={[
            "SN",
            "Center",
            "MRN",
            "Patient Name",
            "Guardian Name",
            "Phone Number",
            "Gross Amount",
            // "Discount %",
            "Discount Amount",
            "Net Amount",
            "Paid Amount",
            "Due Amount",
            "Referring Doctor",
            "Created By",
            "Created At",
          ]}
          data={tableData}
          loading={loading}
          // Pagination props passed to the table component
          height={"50vh"}
          page={page}
          rowsPerPage={rowsPerPage}
          totalRows={totalCount}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          totalAmount={totalNetAmount}
        />
      </Box>
    </Box>
  );
};
export default UserReport;
